import { useLingui } from '@lingui/react/macro';
import {
  type CSSProperties,
  type FC,
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { cn, useIsDesktop } from './react';
import { Spinner } from './Spinner';

export type TableColumn<R> = {
  key: string;
  title?: ReactNode;
  /** Shown below the title in table mode, and above the title in card mode. */
  filter?: ReactNode;
  render: (row: R, index: number, columnKey: string) => ReactNode;
  className?: string;
  /** Prefer `min` for columns that must show full label + icons. */
  width?: 'grow' | 'auto' | 'min' | number;
  mode?: 'card' | 'table' | 'both';
};

/** Structural shape of a `useBackendInfinite` result — the only fields the table reads. */
type RawInfiniteQuery<P> = {
  data?: { response: P; cursor: string | null }[];
  size: number;
  isLoading: boolean;
  isValidating: boolean;
  setSize: (size: number | ((_size: number) => number)) => Promise<unknown>;
};

/** A `useBackendInfinite` result whose page response is the row array. */
export type InfiniteQuery<R> = RawInfiniteQuery<R[]>;

export type TablePagination = {
  onScrollEnd: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
};

type TableCommonProps<R> = {
  columns: TableColumn<R>[];
  rowKey: (r: R, index: number) => string | number;
  className?: string;
  mode?: 'auto' | 'table' | 'card';
  highlight?: (r: R, index: number) => boolean;
  onRowClick?: (row: R, index: number) => void;
  empty?: ReactNode;
};

export type TableProps<R, P = unknown> = TableCommonProps<R> &
  (
    | {
        /** Manual mode — caller owns rows, loading and pagination. */
        rows: R[];
        loading?: boolean;
        pagination?: TablePagination;
        query?: never;
        selectRows?: never;
      }
    | {
        /** SWR mode — each page's response is the row array. */
        query: InfiniteQuery<R>;
        selectRows?: never;
        rows?: never;
        loading?: never;
        pagination?: never;
      }
    | {
        /**
         * SWR mode for endpoints whose response wraps the rows (e.g.
         * `{ transfers: Row[] }`); `selectRows` plucks the array from each page.
         */
        query: RawInfiniteQuery<P>;
        selectRows: (response: P) => R[];
        rows?: never;
        loading?: never;
        pagination?: never;
      }
  );

function useQueryPagination<R>(
  query: RawInfiniteQuery<unknown> | undefined,
  selectRows: (response: unknown) => R[],
): { rows: R[]; loading: boolean; pagination: TablePagination } | null {
  const size = query?.size ?? 1;

  if (!query) return null;
  const { data, isLoading, isValidating, setSize } = query;

  const hasMore = !!data?.[data.length - 1]?.cursor;
  return {
    rows: (data ?? []).flatMap((page) => selectRows(page.response)) ?? [],
    loading: isLoading && size === 1,
    pagination: {
      onScrollEnd: () => {
        if (hasMore) void setSize(size + 1);
      },
      hasMore,
      isLoadingMore: isValidating,
    },
  };
}

const LoadingMore: FC<{
  ref: RefObject<HTMLDivElement | null>;
  loading?: boolean;
}> = ({ ref, loading }) => {
  return (
    <div
      ref={ref}
      className="h-12 flex items-center justify-center"
      aria-hidden="true"
    >
      {loading && <Spinner className="size-5 text-content-tertiary" />}
    </div>
  );
};

export const Table = <R, P = unknown>({
  className,
  columns,
  rows: rowsProp,
  rowKey,
  loading: loadingProp,
  mode: _mode = 'auto',
  highlight,
  onRowClick,
  empty,
  pagination: paginationProp,
  query,
  selectRows,
}: TableProps<R, P>) => {
  const { t } = useLingui();
  const isDesktop = useIsDesktop();
  const derived = useQueryPagination<R>(
    query as RawInfiniteQuery<unknown> | undefined,
    (selectRows ?? ((response) => response)) as (response: unknown) => R[],
  );
  const rows = derived?.rows ?? rowsProp ?? [];
  const loading = derived?.loading ?? loadingProp;
  const pagination = derived?.pagination ?? paginationProp;
  const mode = _mode !== 'auto' ? _mode : isDesktop ? 'table' : 'card';
  const tableRef = useRef<HTMLTableElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    highlight;
    const tm = setTimeout(() => {
      const el = tableRef.current?.querySelector?.('.highlight');
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }, 0);
    return () => clearTimeout(tm);
  }, [highlight, loading]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const infinitePagination = pagination;
    if (
      !sentinel ||
      !infinitePagination?.hasMore ||
      loading ||
      infinitePagination?.isLoadingMore
    )
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          infinitePagination.hasMore &&
          !isLoadingRef.current
        ) {
          isLoadingRef.current = true;
          infinitePagination.onScrollEnd();
        }
      },
      {
        rootMargin: '100px',
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [pagination, loading]);

  useEffect(() => {
    if (!pagination?.isLoadingMore) {
      isLoadingRef.current = false;
    }
  }, [pagination]);

  const tableCols = useMemo(() => {
    const growsColsLength = columns.reduce(
      (p, c) => p + (c.width === 'grow' ? 1 : 0),
      0,
    );
    return columns
      .filter((col) => col.width !== 0 && col.mode !== 'card')
      .map((col) => {
        let style: CSSProperties;
        if (col.width === 'grow') {
          style = {
            width: `${Math.floor(100 / growsColsLength)}%`,
            minWidth: 'auto',
            maxWidth: 'auto',
          };
        } else if (col.width === 'min') {
          style = {
            width: '1%',
            minWidth: 'min-content',
            maxWidth: 'none',
          };
        } else if (typeof col.width === 'number') {
          style = {
            width: col.width,
            minWidth: col.width,
            maxWidth: col.width,
          };
        } else {
          style = {
            width: 'auto',
            minWidth: 'auto',
            maxWidth: 'auto',
          };
        }
        return { ...col, style };
      });
  }, [columns]);

  const initialLoading = loading && rows.length === 0;

  if (mode === 'table') {
    return (
      <>
        <table
          ref={tableRef}
          className={cn(
            'w-full whitespace-nowrap text-xs max-w-full rounded-xl',
            '[&_th,&_td]:py-4 [&_th,&_td]:h-auto [&_th,&_td]:px-2 min-h-20',
            columns.every((col) => col.mode === 'card' || !col.title) &&
              '[&_thead]:!hidden',
            loading && 'shimmer',
            className,
          )}
        >
          {!initialLoading && (
            <thead className="border-b border-border-disabled sticky top-0 z-10 bg-background-primary">
              <tr>
                {tableCols.map((col) => (
                  <th
                    className={cn(
                      'text-start text-content-tertiary font-normal',
                      col.className,
                    )}
                    style={col.style}
                    key={col.key}
                  >
                    {col.title}
                  </th>
                ))}
              </tr>
              {tableCols.some((col) => col.filter != null) && (
                <tr className="border-b border-border-disabled bg-background-primary">
                  {tableCols.map((col) => (
                    <th
                      className={cn(
                        'text-start align-top font-normal py-2 whitespace-normal',
                        col.className,
                      )}
                      style={col.style}
                      key={`${col.key}-filter`}
                    >
                      {col.filter}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
          )}
          <tbody>
            {rows?.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                className={cn(
                  'sticky',
                  highlight?.(row, index) && 'highlight',
                  onRowClick && 'cursor-pointer hover:bg-background-disabled',
                )}
                onClick={() => onRowClick?.(row, index)}
              >
                {tableCols.map((col) => (
                  <td
                    className={cn(col.className)}
                    key={col.key}
                    style={col.style}
                  >
                    {col.render(row, index, col.key)}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={99} className="text-center whitespace-normal">
                  {empty ?? (
                    <>
                      <h3 className="text-base font-normal pt-4">{t`Nothing to See`}</h3>
                      <p className="text-xs text-content-tertiary pb-4">{t`Once there’s data here, it’ll appear right in this table.`}</p>
                    </>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {pagination?.hasMore && (
          <LoadingMore ref={sentinelRef} loading={pagination.isLoadingMore} />
        )}
      </>
    );
  }

  return (
    <>
      <table
        ref={tableRef}
        className={cn(
          'w-full whitespace-nowrap max-w-full rounded-xl',
          '[&_td]:pb-3 [&_td]:h-auto text-xs min-h-20',
          loading && 'shimmer',
          className,
        )}
      >
        <tbody>
          {(() => {
            const cardCols = columns.filter(
              (col) => col.width !== 0 && col.mode !== 'table',
            );
            const showCardFilters = cardCols.some((c) => c.filter != null);
            return (
              <>
                {showCardFilters && (
                  <tr className="bg-background-primary">
                    <td
                      colSpan={99}
                      className="p-3 border-b border-border-disabled whitespace-normal"
                    >
                      <div className="flex flex-col gap-4 desktop:flex-row desktop:flex-wrap desktop:items-end">
                        {cardCols.map((col) =>
                          col.filter ? (
                            <div
                              key={`filter-${col.key}`}
                              className="min-w-0 grow desktop:max-w-[14rem]"
                            >
                              {col.filter}
                            </div>
                          ) : null,
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                {rows?.map((row, index) => (
                  <tr
                    key={rowKey(row, index)}
                    className={cn(
                      highlight?.(row, index) && 'highlight',
                      onRowClick &&
                        'cursor-pointer hover:bg-background-disabled',
                    )}
                    onClick={() => onRowClick?.(row, index)}
                  >
                    <td>
                      <div className="border border-border-disabled rounded-xl p-3 space-y-3">
                        {cardCols.map((col) => (
                          <div
                            className={cn(
                              'flex gap-1 items-center justify-between',
                              col.className,
                            )}
                            key={col.key}
                          >
                            {col.title} {col.render(row, index, col.key)}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </>
            );
          })()}
          {rows.length === 0 && !loading && (
            <tr>
              <td colSpan={99} className="text-center whitespace-normal">
                {empty ?? (
                  <>
                    <h3 className="text-base font-normal pt-6">{t`Nothing to See`}</h3>
                    <p className="text-xs text-content-tertiary pb-6">{t`Once there’s data here, it’ll appear right in this table.`}</p>
                  </>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {pagination?.hasMore && (
        <LoadingMore ref={sentinelRef} loading={pagination.isLoadingMore} />
      )}
    </>
  );
};
